import React, { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { BRANDING_ELIGIBLE_PLAN_IDS } from '../utils/branding';
import CompanyBrandingModal from './CompanyBrandingModal';
import CertificateViewerModal from './CertificateViewerModal';

const planLabels = {
  free: 'Free',
  starter: 'Starter',
  core: 'Core',
  pro: 'Pro',
  proplus: 'Pro+',
};

const planIncludedSeats = {
  free: 1,
  starter: 5,
  core: 10,
  pro: 15,
  proplus: 20,
};

const planLogos = {
  free: '/CrewChainStarter.png',
  starter: '/CrewChainStarter.png',
  core: '/CrewChainCore.png',
  pro: '/CrewChainPro.png',
  proplus: '/CrewChainProPlus.png',
};

const PlanSummary = () => {
  const { user, userData } = useAuth();
  const { refetchCompanyBranding } = useBranding();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);

  useEffect(() => {
    const loadCompany = async () => {
      if (!user || !userData?.companyId) return;
      setLoading(true);
      setError('');
      try {
        const companyRef = doc(db, 'companies', userData.companyId);
        const snap = await getDoc(companyRef);
        if (snap.exists()) {
          setCompany(snap.data());
        } else {
          setError('Company not found');
        }
      } catch (err) {
        console.error('Failed to load company plan info', err);
        setError('Could not load plan information');
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [user, userData?.companyId]);

  if (!user || !userData?.companyId) {
    return null;
  }

  if (loading && !company) {
    return (
      <div style={{
        padding: '16px 20px',
        borderRadius: 12,
        background: 'var(--branding-plan-bar-bg, #0f172a)',
        color: 'var(--branding-plan-bar-text, #e2e8f0)',
        marginBottom: 24,
        fontSize: 14,
      }}>
        Loading plan…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px 20px',
        borderRadius: 12,
        background: 'var(--branding-plan-bar-bg, #0f172a)',
        color: 'var(--branding-plan-bar-error, #fecaca)',
        marginBottom: 24,
        fontSize: 14,
      }}>
        {error}
      </div>
    );
  }

  if (!company) return null;

  const planLabel = planLabels[company.planId] || 'Custom';
  const intervalLabel = company.interval === 'annual' ? 'Annual' : 'Monthly';
  const maxUsers = company.maxUsers ?? 0;
  const pmCount = company.pmCount ?? 0;
  const ownerIds = Array.isArray(company.accountOwnerIds)
    ? company.accountOwnerIds
    : (company.accountOwnerId ? [company.accountOwnerId] : []);
  const isAccountOwner = user?.uid && (ownerIds.includes(user.uid));
  const billingAdminIds = Array.isArray(company.billingAdminIds)
    ? company.billingAdminIds
    : (company.accountOwnerId ? [company.accountOwnerId] : []);
  const isBillingAdmin = user?.uid && billingAdminIds.includes(user.uid);
  const subscriptionStatus = company.subscriptionStatus ?? 'active';
  const paymentNeedsAttention = !!company.paymentNeedsAttention;
  const billingManaged = !!company.billingManaged;
  const canManageBranding =
    isAccountOwner && BRANDING_ELIGIBLE_PLAN_IDS.includes(company.planId || '');
  /** All account owners see this — Pro/Pro+ get the editor; others see upgrade copy in the modal. */
  const canOpenBrandingModal = isAccountOwner;

  const refetchCompany = async () => {
    if (!userData?.companyId) return;
    try {
      const companyRef = doc(db, 'companies', userData.companyId);
      const snap = await getDoc(companyRef);
      if (snap.exists()) setCompany(snap.data());
    } catch (e) {
      console.error(e);
    }
  };

  const handleManageBilling = async () => {
    if (!isBillingAdmin) return;
    setBillingError('');
    setBillingLoading(true);
    try {
      const createBillingPortalSession = httpsCallable(functions, 'createBillingPortalSession');
      const result = await createBillingPortalSession();
      const url = result?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setBillingError('Could not open billing portal. Please try again or contact support.');
      }
    } catch (err) {
      setBillingError(err.message || 'Failed to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const planBarContentStyle = {
    padding: '16px 20px',
    color: 'var(--branding-plan-bar-text, #e2e8f0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  };

  const planBarColumn = (
    <>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--branding-plan-bar-text, #e2e8f0)', marginBottom: 6 }}>
          Plan
        </div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          {planLabel} · {intervalLabel}
        </div>
        {company.name && (
          <div style={{ fontSize: 14, color: 'var(--branding-plan-bar-sub, #cbd5f5)', marginTop: 4 }}>
            {company.name}
          </div>
        )}
      </div>
      {isAccountOwner && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 6,
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--branding-plan-bar-text, #e2e8f0)' }}>Account owner</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowTeamModal(true)}
              style={{
                fontSize: 12,
                padding: '5px 10px',
                borderRadius: 999,
                border: '1px solid var(--branding-plan-bar-button-border, rgba(255,255,255,0.32))',
                background: 'var(--branding-plan-bar-button-bg, rgba(255,255,255,0.14))',
                color: 'var(--branding-plan-bar-button-text, #f8fafc)',
                cursor: 'pointer',
              }}
            >
              Manage team
            </button>
            {canOpenBrandingModal && (
              <button
                type="button"
                onClick={() => setShowBrandingModal(true)}
                style={{
                  fontSize: 12,
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--branding-plan-bar-button-border, rgba(255,255,255,0.32))',
                  background: 'var(--branding-plan-bar-button-bg, rgba(255,255,255,0.14))',
                  color: 'var(--branding-plan-bar-button-text, #f8fafc)',
                  cursor: 'pointer',
                }}
                title={
                  canManageBranding
                    ? 'Custom accent colour for buttons, links, and Home plan strip'
                    : 'Custom accent on Pro and Pro+'
                }
              >
                {canManageBranding ? 'Company branding' : 'Accent & branding'}
              </button>
            )}
            {isBillingAdmin && !billingManaged && (
              <button
                type="button"
                onClick={() => setShowChangePlanModal(true)}
                style={{
                  fontSize: 12,
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--branding-plan-bar-button-border, rgba(255,255,255,0.32))',
                  background: 'var(--branding-plan-bar-button-bg, rgba(255,255,255,0.14))',
                  color: 'var(--branding-plan-bar-button-text, #f8fafc)',
                  cursor: 'pointer',
                }}
              >
                Change plan & seats
              </button>
            )}
            {isBillingAdmin && !billingManaged && (
              <button
                type="button"
                onClick={handleManageBilling}
                disabled={billingLoading}
                style={{
                  fontSize: 12,
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: '1px solid var(--branding-plan-bar-button-border, rgba(255,255,255,0.32))',
                  background: 'var(--branding-plan-bar-button-bg, rgba(255,255,255,0.14))',
                  color: billingLoading
                    ? 'var(--branding-plan-bar-button-text-muted, rgba(248,250,252,0.55))'
                    : 'var(--branding-plan-bar-button-text, #f8fafc)',
                  cursor: billingLoading ? 'wait' : 'pointer',
                }}
              >
                {billingLoading ? 'Opening…' : 'Manage billing'}
              </button>
            )}
          </div>
          {billingError && (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--branding-plan-bar-error, #fecaca)', maxWidth: 260 }}>
              {billingError}
            </div>
          )}
        </div>
      )}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--branding-plan-bar-text, #e2e8f0)', marginBottom: 6 }}>
          Seats
        </div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          {pmCount} / {maxUsers}
        </div>
        {pmCount > maxUsers && (
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--branding-plan-bar-error, #fecaca)' }}>
            Over capacity. Remove users or increase seats.
          </div>
        )}
      </div>
    </>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      {!billingManaged && (paymentNeedsAttention || subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due') && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 12,
          marginBottom: 12,
          background: subscriptionStatus === 'canceled' ? '#422006' : '#431f1f',
          color: '#fef3c7',
          fontSize: 13,
        }}>
          {paymentNeedsAttention && (
            <div>Update your payment method to avoid service interruption.</div>
          )}
          {subscriptionStatus === 'past_due' && !paymentNeedsAttention && (
            <div>Your payment is past due. Please update your payment method.</div>
          )}
          {subscriptionStatus === 'canceled' && (
            <div>Your subscription has been canceled. Re-subscribe to add new users or manage billing.</div>
          )}
          {isBillingAdmin && (paymentNeedsAttention || subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due') && (
            <button
              type="button"
              onClick={handleManageBilling}
              disabled={billingLoading}
              style={{
                marginTop: 8,
                fontSize: 12,
                padding: '6px 12px',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8,
                color: '#fff',
                cursor: billingLoading ? 'wait' : 'pointer',
              }}
            >
              {billingLoading ? 'Opening…' : 'Manage billing'}
            </button>
          )}
        </div>
      )}
      <div
        style={{
          ...planBarContentStyle,
          borderRadius: 12,
          background: 'var(--branding-plan-bar-bg, #0f172a)',
        }}
      >
        {planBarColumn}
      </div>
      {showTeamModal && isAccountOwner && (
        <ManageTeamModal
          companyId={userData.companyId}
          ownerIds={ownerIds}
          currentUserId={user.uid}
          accountOwnerId={company.accountOwnerId || ownerIds[0]}
          billingAdminIds={billingAdminIds}
          billingManaged={billingManaged}
          onClose={() => setShowTeamModal(false)}
          onRefetch={refetchCompany}
        />
      )}
      {showChangePlanModal && isAccountOwner && (
        <ChangePlanModal
          company={company}
          pmCount={pmCount}
          billingManaged={billingManaged}
          onClose={() => setShowChangePlanModal(false)}
          onRefetch={refetchCompany}
        />
      )}
      {showBrandingModal && (
        <CompanyBrandingModal
          open={showBrandingModal}
          onClose={() => setShowBrandingModal(false)}
          onOpenChangePlan={() => {
            setShowBrandingModal(false);
            setShowChangePlanModal(true);
          }}
          onBrandingSaved={() => {
            refetchCompany();
            refetchCompanyBranding();
          }}
        />
      )}
    </div>
  );
};

/** Per-project ownership transfer (account owner offboarding). z-index above Manage team modal. */
function TransferProjectModal({
  project,
  leavingPmId,
  pmList,
  onClose,
  onTransferred,
}) {
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  const collaboratorIds = new Set((project.collaboratorPMs || []).map((c) => c.userId));
  const otherPms = pmList.filter(
    (pm) => pm.id !== leavingPmId && !collaboratorIds.has(pm.id),
  );

  const handleSubmit = async () => {
    if (!selectedNewOwnerId) return;
    setSubmitting(true);
    setLocalError('');
    try {
      const transferProjectOwnership = httpsCallable(functions, 'transferProjectOwnership');
      await transferProjectOwnership({
        projectId: project.projectId,
        newOwnerUserId: selectedNewOwnerId,
      });
      await onTransferred();
      onClose();
    } catch (err) {
      setLocalError(err.message || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const rowStyle = (active) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid var(--branding-app-surface-panel-border)',
    cursor: 'pointer',
    color: active ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-fg)',
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div
        style={{
          background: 'var(--branding-app-surface-panel)',
          color: 'var(--branding-app-surface-panel-fg)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          border: '1px solid var(--branding-app-surface-panel-border)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 style={{ margin: '0 0 8px', fontSize: 16 }}>Transfer project ownership</h4>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--branding-app-surface-panel-muted)' }}>
          <strong style={{ color: 'var(--branding-app-surface-panel-fg)' }}>{project.name}</strong>
          {' · '}
          {project.projectCode}
          {project.status === 'closed' && (
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--branding-app-surface-panel-muted)', fontWeight: 600 }}>(Closed)</span>
          )}
        </p>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--branding-app-surface-panel-muted)' }}>
          Choose a user in your company to become the new project owner. View-only collaborators are not listed here.
        </p>
        {localError && (
          <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{localError}</p>
        )}
        <div style={{ marginBottom: 16 }}>
          {(project.collaboratorPMs || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--branding-app-surface-panel-muted)', marginBottom: 8 }}>
                On this project (collaborators)
              </div>
              {(project.collaboratorPMs || []).map((c) => (
                <label key={c.userId} style={rowStyle(selectedNewOwnerId === c.userId)}>
                  <input
                    type="radio"
                    name="newOwner"
                    checked={selectedNewOwnerId === c.userId}
                    onChange={() => setSelectedNewOwnerId(c.userId)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <div style={{ fontSize: 14 }}>{c.userName || c.userEmail || c.userId}</div>
                    {c.userEmail ? <div style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-muted)' }}>{c.userEmail}</div> : null}
                  </span>
                </label>
              ))}
            </>
          )}
          {otherPms.length > 0 && (
            <>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--branding-app-surface-panel-muted)', margin: '16px 0 8px' }}>
                Other PMs in your company
              </div>
              {otherPms.map((pm) => {
                const label = [pm.firstName, pm.lastName].filter(Boolean).join(' ') || pm.email || pm.id;
                return (
                  <label key={pm.id} style={rowStyle(selectedNewOwnerId === pm.id)}>
                    <input
                      type="radio"
                      name="newOwner"
                      checked={selectedNewOwnerId === pm.id}
                      onChange={() => setSelectedNewOwnerId(pm.id)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <div style={{ fontSize: 14 }}>{label}</div>
                      {pm.email ? <div style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-muted)' }}>{pm.email}</div> : null}
                    </span>
                  </label>
                );
              })}
            </>
          )}
          {(project.collaboratorPMs || []).length === 0 && otherPms.length === 0 && (
            <p style={{ fontSize: 13, color: '#fbbf24' }}>
              No other user is available. Add another user to the company first, or invite a collaborator on this project, then transfer.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            style={{
              fontSize: 12,
              padding: '8px 14px',
              background: 'var(--branding-app-primary-soft)',
              color: 'var(--branding-app-primary)',
              border: '1px solid var(--branding-app-primary-ring)',
              borderRadius: 6,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || !selectedNewOwnerId}
            onClick={handleSubmit}
            style={{
              fontSize: 12,
              padding: '8px 14px',
              background: selectedNewOwnerId ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)',
              color: selectedNewOwnerId ? 'var(--branding-app-primary-fg)' : 'var(--branding-app-surface-panel-muted)',
              border: 'none',
              borderRadius: 6,
              cursor: submitting || !selectedNewOwnerId ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Transferring…' : 'Transfer ownership'}
          </button>
        </div>
      </div>
    </div>
  );
}

const pmInitials = (pm) => {
  const f = pm.firstName?.[0] ?? '';
  const l = pm.lastName?.[0] ?? '';
  return (f + l).toUpperCase() || pm.email?.[0]?.toUpperCase() || '?';
};
const pmAvatarUrl = (pm) => {
  if (pm.profilePictureUrl && pm.profilePictureUrl !== '/crewchainDefault.png') return pm.profilePictureUrl;
  if (pm.photoURL && pm.photoURL !== '/crewchainDefault.png') return pm.photoURL;
  return null;
};
const certLabel = (url) => {
  try {
    const decoded = decodeURIComponent(url.split('?')[0]);
    const raw = decoded.split('/').pop() || 'Certificate';
    return raw.replace(/^\d+_/, '') || raw;
  } catch { return 'Certificate'; }
};

function ManageTeamModal({ companyId, ownerIds, currentUserId, accountOwnerId, billingAdminIds, billingManaged, onClose, onRefetch }) {
  const [pmList, setPmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [billingBusy, setBillingBusy] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [pmForRemoval, setPmForRemoval] = useState(null);
  const [removalImpact, setRemovalImpact] = useState(null);
  const [removalImpactLoading, setRemovalImpactLoading] = useState(false);
  const [transferProject, setTransferProject] = useState(null);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [message, setMessage] = useState('');
  const [certViewer, setCertViewer] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const loadPmList = React.useCallback(async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('companyId', '==', companyId),
        where('role', '==', 'pm')
      );
      const snap = await getDocs(q);
      setPmList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setMessage('Could not load team members');
    }
  }, [companyId]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadPmList().finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [loadPmList]);

  const handleAddOwner = async (targetUserId) => {
    setAdding(targetUserId);
    setMessage('');
    try {
      const addAccountOwner = httpsCallable(functions, 'addAccountOwner');
      await addAccountOwner({ targetUserId });
      setMessage('Added as account owner.');
      if (onRefetch) await onRefetch();
    } catch (err) {
      setMessage(err.message || 'Failed to add owner');
    } finally {
      setAdding(null);
    }
  };

  const refreshRemovalImpact = React.useCallback(async (targetUserId) => {
    const getPmRemovalImpact = httpsCallable(functions, 'getPmRemovalImpact');
    const result = await getPmRemovalImpact({ targetUserId });
    const data = result.data;
    setRemovalImpact({
      ownedProjects: data.ownedProjects || [],
      mustTransferProjects: data.mustTransferProjects,
    });
  }, []);

  const handleRemoveClick = async (pm) => {
    setMessage('');
    setPmForRemoval(pm);
    setRemovalImpact(null);
    setRemovalImpactLoading(true);
    try {
      await refreshRemovalImpact(pm.id);
    } catch (err) {
      setMessage(err.message || 'Could not load project ownership for this PM');
      setPmForRemoval(null);
    } finally {
      setRemovalImpactLoading(false);
    }
  };

  const handleRemoveConfirm = async () => {
    if (!pmForRemoval) return;
    if (removalImpact?.ownedProjects?.length > 0) return;
    const targetUserId = pmForRemoval.id;
    setRemoving(targetUserId);
    setMessage('');
    try {
      const removePmFromCompany = httpsCallable(functions, 'removePmFromCompany');
      await removePmFromCompany({ targetUserId });
      setMessage(`${pmForRemoval.firstName || pmForRemoval.email || 'User'} has been removed from the company.`);
      setPmForRemoval(null);
      setRemovalImpact(null);
      await loadPmList();
      if (onRefetch) await onRefetch();
    } catch (err) {
      setMessage(err.message || 'Failed to remove from company');
    } finally {
      setRemoving(null);
    }
  };

  const handleRemovalPanelClose = () => {
    setPmForRemoval(null);
    setRemovalImpact(null);
    setRemovalImpactLoading(false);
  };

  const handleTransferSuccess = async () => {
    if (pmForRemoval) {
      setRemovalImpactLoading(true);
      try {
        await refreshRemovalImpact(pmForRemoval.id);
      } catch (err) {
        setMessage(err.message || 'Could not refresh project list');
      } finally {
        setRemovalImpactLoading(false);
      }
    }
    setTransferProject(null);
  };

  const isCallerOwner = currentUserId && accountOwnerId && currentUserId === accountOwnerId;
  const isCallerCompanyAdmin = Boolean(currentUserId && ownerIds.includes(currentUserId));

  const handleAddBillingAdmin = async (targetUserId) => {
    setBillingBusy(targetUserId);
    setMessage('');
    try {
      const addBillingAdmin = httpsCallable(functions, 'addBillingAdmin');
      await addBillingAdmin({ targetUserId });
      setMessage('They can now change plan, seats, and open the billing portal.');
      if (onRefetch) await onRefetch();
    } catch (err) {
      setMessage(err.message || 'Failed to add billing admin');
    } finally {
      setBillingBusy(null);
    }
  };

  const handleRemoveBillingAdmin = async (targetUserId) => {
    setBillingBusy(targetUserId);
    setMessage('');
    try {
      const removeBillingAdmin = httpsCallable(functions, 'removeBillingAdmin');
      await removeBillingAdmin({ targetUserId });
      setMessage('Billing access removed for that user.');
      if (onRefetch) await onRefetch();
    } catch (err) {
      setMessage(err.message || 'Failed to remove billing admin');
    } finally {
      setBillingBusy(null);
    }
  };

  const handleTransferOwnership = async () => {
    if (!pendingTransfer) return;
    setTransferring(true);
    setMessage('');
    try {
      const transferAccountOwnership = httpsCallable(functions, 'transferAccountOwnership');
      await transferAccountOwnership({ targetUserId: pendingTransfer.id });
      setMessage('Account ownership transferred.');
      setPendingTransfer(null);
      if (onRefetch) await onRefetch();
    } catch (err) {
      setMessage(err.message || 'Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  const manageTeamBadgeBase = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.03em',
    padding: '4px 10px',
    borderRadius: 999,
    lineHeight: 1.25,
    display: 'inline-block',
  };

  return (
    <>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--branding-app-surface-panel)',
          color: 'var(--branding-app-surface-panel-fg)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 600,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          border: '1px solid var(--branding-app-surface-panel-border)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Manage team</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--branding-app-surface-panel-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-muted)', marginBottom: 8 }}>
          {billingManaged
            ? 'Add admins, or remove users from the company.'
            : 'Add admins, choose who can manage plan and billing (primary owner only), or remove users from the company.'}
        </p>
        <div
          style={{
            fontSize: 12,
            color: 'var(--branding-app-surface-panel-muted)',
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 16,
            rowGap: 10,
          }}
        >
          {[
            {
              pill: { ...manageTeamBadgeBase, background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)' },
              label: 'Owner',
              hint: 'Primary',
            },
            {
              pill: { ...manageTeamBadgeBase, background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)' },
              label: 'Admin',
              hint: 'Company settings',
            },
            ...(billingManaged ? [] : [{
              pill: { ...manageTeamBadgeBase, background: 'rgba(245, 158, 11, 0.2)', color: '#b45309', border: '1px solid rgba(217, 119, 6, 0.5)' },
              label: 'Billing',
              hint: 'Subscription & payment',
            }]),
          ].map((row) => (
            <span key={row.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: 1.2 }}>
              <span style={row.pill}>{row.label}</span>
              <span style={{ color: 'var(--branding-app-surface-panel-muted)' }}>{row.hint}</span>
            </span>
          ))}
        </div>
        {isCallerCompanyAdmin && !isCallerOwner && (() => {
          const primaryOwner = pmList.find(p => p.id === accountOwnerId);
          const primaryName = primaryOwner
            ? ([primaryOwner.firstName, primaryOwner.lastName].filter(Boolean).join(' ') || primaryOwner.email)
            : null;
          return (
            <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 8, background: 'var(--branding-app-surface)', border: '1px solid var(--branding-app-surface-panel-border)', fontSize: 12, color: 'var(--branding-app-surface-panel-muted)', lineHeight: 1.5 }}>
              You have admin access. You can manage the team but only{' '}
              <strong style={{ color: 'var(--branding-app-surface-panel-fg)' }}>{primaryName || 'the primary owner'}</strong>{' '}
              can add or remove billing admins and transfer ownership.
            </div>
          );
        })()}
        {message && <p style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-sub)', marginBottom: 12 }}>{message}</p>}
        {pendingTransfer && (
          <div style={{ marginBottom: 16, padding: 12, background: 'var(--branding-app-surface)', borderRadius: 8, border: '1px solid rgba(217, 119, 6, 0.4)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: '#d97706' }}>
              Transfer primary ownership?
            </p>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--branding-app-surface-panel-sub)' }}>
              <strong style={{ color: 'var(--branding-app-surface-panel-fg)' }}>
                {[pendingTransfer.firstName, pendingTransfer.lastName].filter(Boolean).join(' ') || pendingTransfer.email}
              </strong>{' '}
              will become the primary owner. You cannot undo this without their cooperation.
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--branding-app-surface-panel-muted)' }}>
              You will keep your Admin and Billing access — you can still open the billing portal and change the plan. Only the primary owner can grant or remove billing access for others.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleTransferOwnership}
                disabled={transferring}
                style={{ fontSize: 12, padding: '6px 12px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, cursor: transferring ? 'wait' : 'pointer' }}
              >
                {transferring ? 'Transferring…' : 'Yes, transfer ownership'}
              </button>
              <button
                type="button"
                onClick={() => setPendingTransfer(null)}
                disabled={transferring}
                style={{ fontSize: 12, padding: '6px 12px', background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {pmForRemoval && (
          <div style={{ marginBottom: 16, padding: 12, background: 'var(--branding-app-surface)', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)' }}>
            {removalImpactLoading ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--branding-app-surface-panel-muted)' }}>Checking projects…</p>
            ) : removalImpact?.ownedProjects?.length > 0 ? (
              <>
                <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#d97706' }}>
                  Transfer project ownership first
                </p>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--branding-app-surface-panel-sub)' }}>
                  {[pmForRemoval.firstName, pmForRemoval.lastName].filter(Boolean).join(' ') || pmForRemoval.email} still owns{' '}
                  {removalImpact.ownedProjects.length} project
                  {removalImpact.ownedProjects.length === 1 ? '' : 's'} in your company (including closed ones, so records stay with the firm). Transfer each one to another user, then you can remove them.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                  {removalImpact.ownedProjects.map((p) => (
                    <li
                      key={p.projectId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 0',
                        borderBottom: '1px solid var(--branding-app-surface-panel-border)',
                        fontSize: 13,
                      }}
                    >
                      <span>
                        {p.name}
                        <span style={{ color: 'var(--branding-app-surface-panel-muted)' }}> · {p.projectCode}</span>
                        {p.status === 'closed' && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--branding-app-surface-panel-muted)', fontWeight: 600 }}>Closed</span>
                        )}
                      </span>
                      <button
                        type="button"
                        disabled={!!removing || !!adding}
                        onClick={() => setTransferProject(p)}
                        style={{
                          fontSize: 12,
                          padding: '4px 10px',
                          background: 'var(--branding-app-primary)',
                          color: 'var(--branding-app-primary-fg)',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Transfer…
                      </button>
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleRemovalPanelClose}
                    style={{
                      fontSize: 12,
                      padding: '6px 12px',
                      background: 'var(--branding-app-primary-soft)',
                      color: 'var(--branding-app-primary)',
                      border: '1px solid var(--branding-app-primary-ring)',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 13, marginBottom: 8 }}>
                  Remove {[pmForRemoval.firstName, pmForRemoval.lastName].filter(Boolean).join(' ') || pmForRemoval.email} from the company? They will lose access.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={handleRemoveConfirm} disabled={!!removing} style={{ fontSize: 12, padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: removing ? 'wait' : 'pointer' }}>
                    {removing ? 'Removing…' : 'Remove'}
                  </button>
                  <button type="button" onClick={handleRemovalPanelClose} disabled={!!removing} style={{ fontSize: 12, padding: '6px 12px', background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)', borderRadius: 6, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {transferProject && pmForRemoval && (
          <TransferProjectModal
            project={transferProject}
            leavingPmId={pmForRemoval.id}
            pmList={pmList}
            onClose={() => setTransferProject(null)}
            onTransferred={handleTransferSuccess}
          />
        )}
        {loading ? (
          <p style={{ fontSize: 14, color: 'var(--branding-app-surface-panel-muted)' }}>Loading…</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {pmList.map((pm) => {
              const isOwner = ownerIds.includes(pm.id);
              const isCurrentUser = pm.id === currentUserId;
              const isAccountOwnerRow = accountOwnerId && pm.id === accountOwnerId;
              const isPmBillingAdmin = billingAdminIds.includes(pm.id);
              const canRemoveBillingAdmin = !billingManaged && isCallerOwner && isPmBillingAdmin && billingAdminIds.length > 1;
              const canAddBillingAdmin = !billingManaged && isCallerOwner && !isPmBillingAdmin;
              const displayName = [pm.firstName, pm.lastName].filter(Boolean).join(' ') || pm.email || pm.id;
              const avatarUrl = pmAvatarUrl(pm);
              const initials = pmInitials(pm);
              const certs = Array.isArray(pm.certificationUrls) ? pm.certificationUrls : [];
              return (
                <li
                  key={pm.id}
                  style={{
                    padding: '12px 10px',
                    borderBottom: '1px solid var(--branding-app-surface-panel-border)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    borderRadius: 0,
                    background: 'transparent',
                  }}
                >
                  {/* Avatar */}
                  <div
                    onClick={() => avatarUrl && setLightbox({ url: avatarUrl, name: displayName })}
                    style={{ flexShrink: 0, width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', background: avatarUrl ? 'transparent' : 'var(--branding-app-surface-panel-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, color: 'var(--branding-app-surface-panel-fg)', cursor: avatarUrl ? 'pointer' : 'default' }}
                  >
                    {avatarUrl
                      ? <img src={avatarUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : initials
                    }
                  </div>

                  {/* Name + email + certs */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--branding-app-surface-panel-fg)' }}>{displayName}</span>
                      {isCurrentUser && <span style={{ fontSize: 11, color: 'var(--branding-app-surface-panel-muted)', fontStyle: 'italic' }}>you</span>}
                    </div>
                    {pm.email && (
                      <div style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-muted)', marginBottom: certs.length > 0 ? 6 : 0 }}>{pm.email}</div>
                    )}
                    {certs.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {certs.map((url, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setCertViewer({ url, title: certLabel(url) })}
                            style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)', cursor: 'pointer', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={certLabel(url)}
                          >
                            {certLabel(url)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: 'var(--branding-app-surface-panel-muted)', fontStyle: 'italic' }}>No certs uploaded</div>
                    )}
                  </div>

                  {/* Badges + actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {isAccountOwnerRow && (
                        <span style={{ ...manageTeamBadgeBase, background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)' }}>Owner</span>
                      )}
                      {!isAccountOwnerRow && isOwner && (
                        <span style={{ ...manageTeamBadgeBase, background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)' }}>Admin</span>
                      )}
                      {!billingManaged && isPmBillingAdmin && (
                        <span style={{ ...manageTeamBadgeBase, background: 'rgba(245, 158, 11, 0.2)', color: '#b45309', border: '1px solid rgba(217, 119, 6, 0.5)' }}>Billing</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {!isOwner && (
                        <button
                          type="button"
                          disabled={!!adding || !!billingBusy || transferring}
                          onClick={() => handleAddOwner(pm.id)}
                          style={{ fontSize: 11, padding: '3px 9px', background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)', borderRadius: 6, cursor: adding || billingBusy || transferring ? 'wait' : 'pointer' }}
                        >
                          {adding === pm.id ? 'Updating…' : 'Make admin'}
                        </button>
                      )}
                      {canAddBillingAdmin && (
                        <button
                          type="button"
                          disabled={!!billingBusy || !!adding || transferring}
                          onClick={() => handleAddBillingAdmin(pm.id)}
                          style={{ fontSize: 11, padding: '3px 9px', background: 'var(--branding-app-primary-soft)', color: 'var(--branding-app-primary)', border: '1px solid var(--branding-app-primary-ring)', borderRadius: 6, cursor: billingBusy || adding || transferring ? 'wait' : 'pointer' }}
                        >
                          {billingBusy === pm.id ? 'Updating…' : 'Add billing'}
                        </button>
                      )}
                      {canRemoveBillingAdmin && (
                        <button
                          type="button"
                          disabled={!!billingBusy || !!adding || transferring}
                          onClick={() => handleRemoveBillingAdmin(pm.id)}
                          style={{ fontSize: 11, padding: '3px 9px', background: 'transparent', color: 'var(--branding-app-surface-panel-muted)', border: '1px solid var(--branding-app-surface-panel-border)', borderRadius: 6, cursor: billingBusy || adding || transferring ? 'wait' : 'pointer' }}
                        >
                          {billingBusy === pm.id ? 'Updating…' : 'Remove billing'}
                        </button>
                      )}
                      {isCallerOwner && !isAccountOwnerRow && (
                        <button
                          type="button"
                          disabled={transferring || !!adding || !!billingBusy || !!removing}
                          onClick={() => { setPmForRemoval(null); setPendingTransfer(pm); }}
                          style={{ fontSize: 11, padding: '3px 9px', background: 'transparent', color: '#b45309', border: '1px solid rgba(217, 119, 6, 0.6)', borderRadius: 6, cursor: transferring || adding || billingBusy || removing ? 'wait' : 'pointer' }}
                        >
                          Transfer
                        </button>
                      )}
                      {!isCurrentUser && !(isOwner && ownerIds.length === 1) && !isAccountOwnerRow && (
                        <button
                          type="button"
                          disabled={!!removing || !!adding || !!billingBusy || transferring}
                          onClick={() => { setPendingTransfer(null); handleRemoveClick(pm); }}
                          style={{ fontSize: 11, padding: '3px 9px', background: 'transparent', color: '#f87171', border: '1px solid #f87171', borderRadius: 6, cursor: removing || adding || billingBusy || transferring ? 'not-allowed' : 'pointer' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
    {certViewer && (
      <CertificateViewerModal
        open={!!certViewer}
        onClose={() => setCertViewer(null)}
        certificateUrl={certViewer.url}
        title={certViewer.title}
      />
    )}
    {lightbox && (
      <div
        onClick={() => setLightbox(null)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1100, cursor: 'zoom-out', padding: 24 }}
      >
        <img
          src={lightbox.url}
          alt={lightbox.name}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', cursor: 'default' }}
        />
        {lightbox.name && (
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{lightbox.name}</div>
        )}
      </div>
    )}
    </>
  );
}

function ChangePlanModal({ company, pmCount, billingManaged, onClose, onRefetch }) {
  const [selectedPlan, setSelectedPlan] = useState(company.planId || 'core');
  const [selectedInterval, setSelectedInterval] = useState(company.interval === 'annual' ? 'annual' : 'monthly');
  const [seatCount, setSeatCount] = useState(() => {
    let included = planIncludedSeats[company.planId];
    if (included == null) {
      included = company.maxUsers != null ? company.maxUsers : (pmCount || 1);
    }
    return Math.max(pmCount, included);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirming, setConfirming] = useState(false);

  const plans = [
    { id: 'starter', name: 'Starter', description: 'Get started fast.' },
    { id: 'core', name: 'Core', description: 'For growing teams.', addOnMonthly: 'Add seats later – £20/user', addOnAnnual: 'Add seats later – £220/user/year (1 month free)' },
    { id: 'pro', name: 'Pro', description: 'Includes custom branding.', addOnMonthly: 'Add seats later – £18/user', addOnAnnual: 'Add seats later – £198/user/year (1 month free)' },
    { id: 'proplus', name: 'Pro+', description: 'Includes custom branding.', addOnMonthly: 'Add seats later – £16/user', addOnAnnual: 'Add seats later – £176/user/year (1 month free)' },
  ];

  const handlePlanSelect = (planId) => {
    const included = planIncludedSeats[planId] ?? 0;

    // Starter cannot have add-on seats; if current project managers exceed included, block selection
    if (planId === 'starter' && pmCount > included) {
      const diff = pmCount - included;
      setError(`Starter supports up to ${included} users. You currently have ${pmCount}. Remove ${diff} user${diff === 1 ? '' : 's'} first to move to Starter.`);
      setMessage('');
      return;
    }

    setSelectedPlan(planId);
    const nextMin = Math.max(pmCount, included);
    setSeatCount(prev => Math.max(prev, nextMin));
    setError('');
    setMessage('');
  };

  const handleSeatChange = (value) => {
    const num = parseInt(value, 10);
    if (Number.isNaN(num)) {
      setSeatCount(pmCount);
      return;
    }
    const minSeats = Math.max(pmCount, planIncludedSeats[selectedPlan] ?? 0);
    if (num < minSeats) {
      // If they go below active project managers, show a one-off popup and reset
      if (num < pmCount) {
        // eslint-disable-next-line no-alert
        window.alert(`You currently have ${pmCount} active user${pmCount === 1 ? '' : 's'}. You cannot choose fewer seats than your active users.`);
      }
      setSeatCount(minSeats);
      setError('');
      setMessage('');
      return;
    }
    setSeatCount(num);
    setError('');
    setMessage('');
  };

  const handleSave = async () => {
    if (billingManaged) {
      setError('Billing for this account is managed directly — please contact us to change your plan or seats.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const requestPlanChange = httpsCallable(functions, 'requestPlanChange');
      await requestPlanChange({ planId: selectedPlan, seatCount, interval: selectedInterval });
      setMessage('Plan and seats updated. Changes may take a moment to reflect.');
      if (onRefetch) {
        await onRefetch();
      }
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.message || 'Failed to update plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const currentIncluded = planIncludedSeats[selectedPlan] ?? 0;
  const extraSeats = Math.max(0, seatCount - currentIncluded);

  const currentPlanLabel = planLabels[company.planId] || 'Custom';
  const currentIntervalLabel = company.interval === 'annual' ? 'Annual' : 'Monthly';
  const isAnnual = selectedInterval === 'annual';

  const planPricing = {
    starter: { monthly: 129, annual: 129 * 11 },
    core: { monthly: 179, annual: 179 * 11 },
    pro: { monthly: 209, annual: 209 * 11 },
    proplus: { monthly: 259, annual: 259 * 11 },
  };
  const addonUnitPricing = {
    core: { monthly: 20, annual: 220 },
    pro: { monthly: 18, annual: 198 },
    proplus: { monthly: 16, annual: 176 },
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div
        style={{
          background: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 16,
          padding: 24,
          width: 'min(1100px, calc(100vw - 48px))',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 20 }}>Change plan & seats</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--branding-app-surface-panel-hint)' }}>
              Current plan: {currentPlanLabel} · {currentIntervalLabel}. You have {pmCount} active user{pmCount === 1 ? '' : 's'} and {company.maxUsers ?? 0} seats.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--branding-app-surface-panel-hint)',
              cursor: saving ? 'default' : 'pointer',
              fontSize: 22,
            }}
          >
            ×
          </button>
        </div>

        {billingManaged && (
          <div style={{
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.3)',
            color: '#c7d2fe',
            fontSize: 13,
            marginBottom: 20,
          }}>
            Billing for this account is managed directly — please contact us to change your plan or seats.
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cc-text-muted)', marginBottom: 8 }}>
            Billing period
          </div>
          <div style={{ display: 'inline-flex', background: '#1e293b', borderRadius: 10, padding: 4 }}>
            <button
              type="button"
              onClick={() => setSelectedInterval('monthly')}
              style={{
                padding: '8px 14px',
                border: 'none',
                borderRadius: 8,
                background: selectedInterval === 'monthly' ? '#0f172a' : 'transparent',
                color: selectedInterval === 'monthly' ? '#e2e8f0' : '#94a3b8',
                fontSize: 13,
                fontWeight: selectedInterval === 'monthly' ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setSelectedInterval('annual')}
              style={{
                padding: '8px 14px',
                border: 'none',
                borderRadius: 8,
                background: selectedInterval === 'annual' ? '#0f172a' : 'transparent',
                color: selectedInterval === 'annual' ? '#e2e8f0' : '#94a3b8',
                fontSize: 13,
                fontWeight: selectedInterval === 'annual' ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              Annual <span style={{ fontSize: 12, color: selectedInterval === 'annual' ? '#e2e8f0' : '#64748b' }}>(1 month free)</span>
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cc-text-muted)', marginBottom: 8 }}>
            Choose plan
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'nowrap', overflowX: 'hidden', alignItems: 'flex-start' }}>
            {plans.map(plan => {
              const selected = plan.id === selectedPlan;
              const logoSrc = planLogos[plan.id];
              const included = planIncludedSeats[plan.id];
              const isCurrentPlan = plan.id === (company.planId || 'core');
              const price = planPricing[plan.id]?.[isAnnual ? 'annual' : 'monthly'];
              const annualMonthlyEquivalent = planPricing[plan.id]
                ? Math.round(planPricing[plan.id].annual / 12)
                : null;
              const addOnLine =
                plan.id !== 'starter' && ((isAnnual && plan.addOnAnnual) || (!isAnnual && plan.addOnMonthly))
                  ? (isAnnual ? plan.addOnAnnual : plan.addOnMonthly)
                  : null;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => handlePlanSelect(plan.id)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'center',
                    padding: 16,
                    borderRadius: 16,
                    border: selected ? '2px solid #38bdf8' : '1px solid #1e293b',
                    background: '#020617',
                    cursor: 'pointer',
                    minWidth: 180,
                    flex: '1 1 0',
                    minHeight: 0,
                  }}
                >
                  {logoSrc && (
                    <div style={{
                      marginBottom: 12,
                      minHeight: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        width: 160,
                        height: 160,
                        borderRadius: 12,
                        overflow: 'hidden',
                        lineHeight: 0,
                        background: plan.id === 'pro' ? '#000000' : '#0b1220',
                        border: '1px solid #0f172a',
                      }}>
                        <img
                          src={logoSrc}
                          alt={plan.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            display: 'block',
                            ...(plan.id === 'pro'
                              ? {
                                  transform: 'scale(0.88)',
                                  transformOrigin: 'center center',
                                }
                              : {}),
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {typeof price === 'number' && (
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>
                        £{isAnnual ? price.toLocaleString('en-GB') : price}
                      </span>
                      <span style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-hint)', marginLeft: 6 }}>
                        {isAnnual ? '/year' : '/month'}
                      </span>
                      {isAnnual && typeof annualMonthlyEquivalent === 'number' && (
                        <div style={{ fontSize: 12, color: 'var(--cc-text-muted)', marginTop: 2 }}>
                          £{annualMonthlyEquivalent}/month when billed annually
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ height: 20, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>
                    {isCurrentPlan && (
                      <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#38bdf8',
                        border: '1px solid rgba(56,189,248,0.55)',
                        background: 'rgba(56,189,248,0.10)',
                        padding: '2px 8px',
                        borderRadius: 999,
                        lineHeight: '16px',
                      }}>
                        Current plan
                      </div>
                    )}
                  </div>
                  {typeof included === 'number' && (
                    <div style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-hint)', marginTop: 6 }}>
                      Includes up to {included} user{included === 1 ? '' : 's'}
                    </div>
                  )}
                  <div style={{ fontSize: 13, color: 'var(--cc-text-muted)', marginTop: 6 }}>
                    {plan.description}
                  </div>
                  {/* Fixed slot so plan logos align: Starter has no add-on copy but same vertical space as others */}
                  <div
                    style={{
                      minHeight: 56,
                      marginTop: 10,
                      fontSize: 13,
                      color: 'var(--branding-app-surface-panel-hint)',
                      lineHeight: 1.35,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                    }}
                  >
                    {addOnLine ? <span>{addOnLine}</span> : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cc-text-muted)', marginBottom: 4 }}>
            Total seats
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="number"
              min={Math.max(pmCount, currentIncluded)}
              max={selectedPlan === 'starter' ? currentIncluded : undefined}
              value={seatCount}
              onChange={(e) => handleSeatChange(e.target.value)}
              disabled={selectedPlan === 'starter'}
              style={{
                width: 90,
                padding: '6px 8px',
                borderRadius: 8,
                border: '1px solid #334155',
                background: selectedPlan === 'starter' ? '#02061780' : '#020617',
                color: '#e2e8f0',
                fontSize: 13,
              }}
            />
            <div style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)' }}>
              {currentIncluded > 0 && selectedPlan !== 'starter' && (
                <>
                  {currentIncluded} included, {extraSeats} extra seat{extraSeats === 1 ? '' : 's'}.
                </>
              )}
              {currentIncluded > 0 && selectedPlan === 'starter' && (
                <>Starter includes up to {currentIncluded} user{currentIncluded === 1 ? '' : 's'}. Extra seats are not available on Starter.</>
              )}
              {currentIncluded === 0 && (
                <>Total seats: {seatCount}.</>
              )}
            </div>
          </div>
        </div>

        {(() => {
          const base = planPricing[selectedPlan]?.[isAnnual ? 'annual' : 'monthly'] ?? null;
          const unit = addonUnitPricing[selectedPlan]?.[isAnnual ? 'annual' : 'monthly'] ?? 0;
          const total = typeof base === 'number' ? (base + (unit * extraSeats)) : null;
          if (total == null) return null;
          return (
            <div style={{
              marginBottom: 14,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid #1e293b',
              background: '#020617',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}>
              <div style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)' }}>
                Estimated total ({isAnnual ? 'annual' : 'monthly'})
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>
                £{isAnnual ? total.toLocaleString('en-GB') : total}
                <span style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)', marginLeft: 6 }}>
                  {isAnnual ? '/year' : '/month'}
                </span>
              </div>
            </div>
          );
        })()}

        <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--branding-app-surface-panel-hint)' }}>
          <div>
            You&apos;re changing to <strong>{planLabels[selectedPlan] || 'Custom'}</strong> with{' '}
            <strong>{seatCount}</strong> seat{seatCount === 1 ? '' : 's'}.
          </div>
          <div style={{ marginTop: 2 }}>
            Stripe will apply any price difference automatically on your next invoice.
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: 10, fontSize: 12, color: '#fecaca' }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: 10, fontSize: 12, color: '#bbf7d0' }}>
            {message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              fontSize: 13,
              padding: '6px 12px',
              background: 'transparent',
              border: '1px solid #334155',
              borderRadius: 8,
              color: '#e2e8f0',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirming) {
                handleSave();
              } else {
                setConfirming(true);
              }
            }}
            disabled={saving || billingManaged}
            style={{
              fontSize: 13,
              padding: '6px 14px',
              background: billingManaged ? '#475569' : '#38bdf8',
              border: 'none',
              borderRadius: 8,
              color: 'var(--cc-text-primary)',
              fontWeight: 600,
              cursor: billingManaged ? 'not-allowed' : (saving ? 'wait' : 'pointer'),
            }}
          >
            {saving ? 'Updating…' : (confirming ? 'Click again to confirm' : 'Update plan')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlanSummary;

